import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Hooks, XtLocale, Util } from "@xt/xtbase";
import {
  fetchAcademyConfigListParsed,
  resolveAcademyConfigItemLink,
  resolveAcademyConfigAssetUrl,
} from "@/api/academy-config";
import { getNewsList } from "@/api/xt-app";
import { observer } from "mobx-react-lite";
import styles from "./index.module.less";
import iconAnnouncementBodyDown from "assets/images/home/announcement-body-down.svg";
import iconAnnouncementBodyUp from "assets/images/home/announcement-body-up.svg";
import iconAnnouncementItemTitle from "assets/images/home/announcement-item-title-icon.svg";

const { useTranslation } = Hooks;
const { moment } = Util;

/** SSR：服务端用 useEffect，避免 useLayoutEffect 告警 */
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** 公告块顶边对齐视口高度比例（与 scrollAnnouncementIntoView 一致） */
const ANCHOR_VIEWPORT_RATIO = 0.36;

const TAB_KEYS = {
  ANNOUNCEMENT: "announcement",
  NEWS: "news",
  ACADEMY: "academy",
};

/** 「查看更多」仍指向 Zendesk 分类页；列表数据已走 xt-app `/public/academy-config/list` */
const ZENDESK_CATEGORY = {
  ANNOUNCEMENT: "5284067920670-Announcements",
  ACADEMY: "5284051234846-Academy",
};

/*
 * 原首页底部 Announcement / Spikex Academy 列表数据源（Zendesk Help Center articles API，已替换为
 * GET `${origin}/xt-app/public/academy-config/list` + configType=2|1，见 @/api/academy-config）。
 *
 * Announcement 原接口：
 * https://spikex.zendesk.com/api/v2/help_center/{lang}/categories/5284067920670-Announcements/articles.json?sort_by=created_at&sort_order=desc
 *
 * Academy 原接口：
 * https://spikex.zendesk.com/api/v2/help_center/{lang}/categories/5284051234846-Academy/articles.json?sort_by=created_at&sort_order=desc
 */

/** 底部常驻 dockBottom → 随区块滚动 flow → 触顶贴在顶栏下方（PIN_TOP） */
const TAB_MODE = {
  DOCK_BOTTOM: "dockBottom",
  FLOW: "flow",
  PIN_TOP: "pinTop",
};

/**
 * 与锚点滚动一致：视口内大致留给公告「内容区」body 的纵向空间。
 * dock 时减去底栏；再减去 tab 条高度（锚点落在根节点顶边，其下还有 tab）。
 */
function getDefaultAnnouncementContentBandPx(tabMode, tabBarHeightPx) {
  if (typeof window === "undefined") return 0;
  const vh = window.innerHeight;
  const anchorTop = vh * ANCHOR_VIEWPORT_RATIO;
  const bottomUi =
    tabMode === TAB_MODE.DOCK_BOTTOM ? getBottomBarHeightPx() : 0;
  const tabH = Math.max(40, tabBarHeightPx || 56);
  return Math.max(100, vh - anchorTop - tabH - bottomUi - 24);
}

/** 内容区高度大于默认可视带时不再锚点滚页；测不到时保守为「需要滚」 */
function shouldScrollAnnouncementToAnchor(
  tabMode,
  bodyScrollHeight,
  tabBarHeightPx
) {
  if (typeof window === "undefined") return false;
  if (!bodyScrollHeight || bodyScrollHeight <= 0) return true;
  const band = getDefaultAnnouncementContentBandPx(tabMode, tabBarHeightPx);
  return bodyScrollHeight <= band;
}

function getBottomBarHeightPx() {
  if (typeof document === "undefined") return 56;
  const el = document.querySelector("[data-app-bottom-tab]");
  if (el) {
    const h = el.getBoundingClientRect().height;
    if (h > 0) return Math.round(h);
  }
  return 56;
}

function getSafeAreaInsetTopPx() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:absolute;left:-9999px;visibility:hidden;padding-top:env(safe-area-inset-top,0px)";
  document.body.appendChild(probe);
  const safe = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  document.body.removeChild(probe);
  return safe;
}

/** 窄屏顶部导航下沿 Y（与 `@xt/Nav` `.app-navbar-mb` 高度 52px + 安全区一致），用于 dock 临界与 PIN 进入阈值 */
function getHeaderBottomPx() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 80;
  }
  const mobile = window.matchMedia("(max-width: 767px)").matches;
  if (!mobile) return 80;
  return 52 + getSafeAreaInsetTopPx();
}

/**
 * @param {number} slotTop tab 占位顶部相对视口的 Y
 * @param {number} dockTopLine 与底部常驻条对齐时占位顶部临界 Y
 * @param {number} pinThresholdY 顶栏下沿 Y：占位顶边滚到此处即进入 PIN（避免正文仍从 nav 下方穿过被盖住）
 * @param {string} prev 上一帧模式
 */
function computeTabMode(slotTop, dockTopLine, pinThresholdY, prev) {
  const D = 12;
  const P = 12;

  if (prev === TAB_MODE.DOCK_BOTTOM) {
    if (slotTop <= dockTopLine - D) {
      return slotTop <= pinThresholdY ? TAB_MODE.PIN_TOP : TAB_MODE.FLOW;
    }
    return TAB_MODE.DOCK_BOTTOM;
  }
  if (prev === TAB_MODE.FLOW) {
    if (slotTop > dockTopLine + D) return TAB_MODE.DOCK_BOTTOM;
    if (slotTop <= pinThresholdY) return TAB_MODE.PIN_TOP;
    return TAB_MODE.FLOW;
  }
  if (prev === TAB_MODE.PIN_TOP) {
    if (slotTop > pinThresholdY + P) {
      return slotTop > dockTopLine + D ? TAB_MODE.DOCK_BOTTOM : TAB_MODE.FLOW;
    }
    return TAB_MODE.PIN_TOP;
  }
  if (slotTop > dockTopLine + D) return TAB_MODE.DOCK_BOTTOM;
  if (slotTop <= pinThresholdY) return TAB_MODE.PIN_TOP;
  return TAB_MODE.FLOW;
}

/** DOCK 与 PIN 下 tab 条 `padding-bottom` 差（与 index.module.less 一致），收起时用 DOCK 高度算 dock 线 */
const TAB_BAR_PIN_TO_DOCK_HEIGHT_DELTA = 20;

/** 与 tick 内一致：未滚进公告区时占位块顶边临界值及进入 DOCK 的 slotTop 下限 */
function getAnnouncementDockLayout(tabBarHeightPx) {
  const H = Math.max(40, tabBarHeightPx || 56);
  const vh = window.innerHeight;
  const barH = getBottomBarHeightPx();
  const rawDockLine = vh - barH - H;
  const headerB = getHeaderBottomPx();
  const dockTopLine = Math.max(rawDockLine, headerB + 8);
  return { vh, dockTopLine, headerB, H };
}

function formatDayLabel(dayKey, langCode) {
  const m = moment(dayKey, "YYYY-MM-DD");
  if (!m.isValid()) return dayKey;
  if (String(langCode || "")
    .toLowerCase()
    .startsWith("zh")) {
    return m.format("M月D日");
  }
  return m.format("ll");
}

/** 接口 createdTime：多为毫秒时间戳，少数为秒级；与旧 Zendesk 列表「日期 + 时间」展示一致 */
function formatAcademyConfigCreatedTime(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return "";
  const ms = n < 1e12 ? n * 1000 : n;
  const m = moment(ms);
  if (!m.isValid()) return "";
  return m.format("YYYY-MM-DD HH:mm");
}

const HomeAnnouncement = () => {
  const t = useTranslation();

  const [activeTab, setActiveTab] = useState(TAB_KEYS.ANNOUNCEMENT);

  const [announcementList, setAnnouncementList] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [academyList, setAcademyList] = useState([]);

  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [academyLoading, setAcademyLoading] = useState(false);

  const mapAnnouncementRowsToListItems = (rows) =>
    (rows || [])
      .slice()
      .sort((a, b) => Number(b.weights || 0) - Number(a.weights || 0))
      .map((row) => ({
        id: row.id,
        title: row.title || "",
        date: formatAcademyConfigCreatedTime(row.createdTime),
        url: resolveAcademyConfigItemLink(row),
      }));

  /** 学院 tab：对齐 Figma「学院控件」——标题、tags[]、右侧 coverImage（无则用 icon） */
  const mapAcademyRowsToListItems = (rows) =>
    (rows || [])
      .slice()
      .sort((a, b) => Number(b.weights || 0) - Number(a.weights || 0))
      .map((row) => {
        const tags = Array.isArray(row.tags)
          ? row.tags.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean)
          : [];
        const coverRaw =
          row.coverImage != null && String(row.coverImage).trim()
            ? String(row.coverImage).trim()
            : "";
        const coverImageSrc = resolveAcademyConfigAssetUrl(coverRaw);
        return {
          id: row.id,
          title: row.title || "",
          date: "",
          url: resolveAcademyConfigItemLink(row),
          tags,
          coverImageSrc,
        };
      });

  const loadAnnouncementAndAcademy = useCallback(async () => {
    try {
      setAnnouncementLoading(true);
      setAcademyLoading(true);
      const [annRows, acaRows] = await Promise.all([
        fetchAcademyConfigListParsed({ configType: 2, platform: "web" }),
        fetchAcademyConfigListParsed({ configType: 1, platform: "web" }),
      ]);
      setAnnouncementList(mapAnnouncementRowsToListItems(annRows));
      setAcademyList(mapAcademyRowsToListItems(acaRows));
    } catch (error) {
      console.error("Failed to load academy-config lists:", error);
      setAnnouncementList([]);
      setAcademyList([]);
    } finally {
      setAnnouncementLoading(false);
      setAcademyLoading(false);
    }
  }, []);

  const loadNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      const res = await getNewsList({
        params: { pageIndex: 1, pageSize: 999 },
      });
      const raw = Array.isArray(res) ? res : (res && res.data) || [];
      const list = raw.map((n) => {
        const ts = n.publishedAt || n.createdAt || Date.now();
        return {
          id: n.id,
          title: n.title,
          url: n.url,
          publishedAt: ts,
          timeStr: moment(ts).format("HH:mm"),
          dayKey: moment(ts).format("YYYY-MM-DD"),
        };
      });
      setNewsList(list);
    } catch (error) {
      console.error("Failed to load news:", error);
      setNewsList([]);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncementAndAcademy();
    loadNews();
  }, [loadAnnouncementAndAcademy, loadNews]);

  const langCode = (XtLocale.intl && XtLocale.intl.locale) || "en";

  const newsGrouped = useMemo(() => {
    if (!newsList.length) return [];
    const byDay = new Map();
    for (const item of newsList) {
      const dayKey = item.dayKey;
      if (!byDay.has(dayKey)) byDay.set(dayKey, []);
      byDay.get(dayKey).push(item);
    }
    const keys = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
    return keys.map((dayKey) => ({
      dayKey,
      label: formatDayLabel(dayKey, langCode),
      items: byDay.get(dayKey).sort((a, b) => b.publishedAt - a.publishedAt),
    }));
  }, [newsList, langCode]);

  const tabs = useMemo(
    () => [
      {
        key: TAB_KEYS.ANNOUNCEMENT,
        label: t("home.announcement") || "Announcement",
      },
      { key: TAB_KEYS.NEWS, label: t("home.news") || "News" },
      { key: TAB_KEYS.ACADEMY, label: t("home.academy") || "Spikex Academy" },
    ],
    [t]
  );

  const getMoreUrl = (tabKey) => {
    const zenLang = XtLocale.getZenLan();
    if (tabKey === TAB_KEYS.ANNOUNCEMENT) {
      // 原「查看更多」完整链接（保留备查；未删除）：
      // return `https://spikex.zendesk.com/hc/${zenLang}/categories/5284067920670-Announcements`;
      return `https://spikex.zendesk.com/hc/${zenLang}/categories/${ZENDESK_CATEGORY.ANNOUNCEMENT}`;
    }
    if (tabKey === TAB_KEYS.ACADEMY) {
      // 原「查看更多」完整链接（保留备查；未删除）：
      // return `https://spikex.zendesk.com/hc/${zenLang}/categories/5284051234846-Academy`;
      return `https://spikex.zendesk.com/hc/${zenLang}/categories/${ZENDESK_CATEGORY.ACADEMY}`;
    }
    return "";
  };

  const currentState = useMemo(() => {
    switch (activeTab) {
      case TAB_KEYS.NEWS:
        return { list: newsList, loading: newsLoading };
      case TAB_KEYS.ACADEMY:
        return { list: academyList, loading: academyLoading };
      case TAB_KEYS.ANNOUNCEMENT:
      default:
        return { list: announcementList, loading: announcementLoading };
    }
  }, [
    activeTab,
    announcementList,
    announcementLoading,
    newsList,
    newsLoading,
    academyList,
    academyLoading,
  ]);

  const onMoreClick = () => {
    const url = getMoreUrl(activeTab);
    if (url) window.open(url, "_blank");
  };

  const openUrl = (url) => {
    if (url) window.open(url, "_blank");
  };

  const announcementRootRef = useRef(null);
  const tabSlotRef = useRef(null);
  const tabBarRef = useRef(null);
  const bodyRef = useRef(null);
  const bodyInViewportRef = useRef(false);
  const tabModeRef = useRef(TAB_MODE.DOCK_BOTTOM);
  const [tabMode, setTabMode] = useState(TAB_MODE.DOCK_BOTTOM);
  const [tabBarHeight, setTabBarHeight] = useState(0);
  /** 内容区是否在视口内（随滚动更新，用于切换上/下箭头；展开/收起仅驱动滚动） */
  const [bodyInViewport, setBodyInViewport] = useState(false);
  /** 用户刚点过收起：在 IO 仍认为 body 可见时，也先显示「展开」箭头 */
  const [postCollapseExpectExpand, setPostCollapseExpectExpand] =
    useState(false);

  useEffect(() => {
    if (!bodyInViewport) {
      setPostCollapseExpectExpand(false);
    }
  }, [bodyInViewport]);

  const scrollAnnouncementIntoView = useCallback(() => {
    if (typeof window === "undefined") return;
    const root = announcementRootRef.current;
    if (!root) return;
    const vh = window.innerHeight;
    const anchorFromViewportTop = vh * ANCHOR_VIEWPORT_RATIO;
    const docTop = root.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: Math.max(0, docTop - anchorFromViewportTop),
      behavior: "smooth",
    });
  }, []);

  /**
   * 用户主动切 tab / 点展开：除「吸顶 + 内容高于默认可视带」外一律锚点滚到公告区，
   * 避免长列表在 PIN_TOP 时反复把页面往下带；dock/flow 下长列表仍需能滚到展开位。
   */
  const maybeScrollAnnouncementToAnchor = useCallback(() => {
    const mode = tabModeRef.current;
    const h = bodyRef.current?.scrollHeight ?? 0;
    const tabH = tabBarRef.current?.offsetHeight || 56;
    if (mode === TAB_MODE.PIN_TOP) {
      if (!shouldScrollAnnouncementToAnchor(mode, h, tabH)) return;
    }
    scrollAnnouncementIntoView();
  }, [scrollAnnouncementIntoView]);

  /** 与滚动 tick 相同：根据占位 slot 几何更新 tabMode（供 layout / 收起后同步） */
  const recomputeTabModeOnce = useCallback(() => {
    const slot = tabSlotRef.current;
    if (!slot) return;
    const H = tabBarRef.current?.offsetHeight || tabBarHeight || 56;
    const { dockTopLine } = getAnnouncementDockLayout(H);
    const pinThresholdY = getHeaderBottomPx();
    const slotTop = slot.getBoundingClientRect().top;
    const next = computeTabMode(
      slotTop,
      dockTopLine,
      pinThresholdY,
      tabModeRef.current
    );
    if (next !== tabModeRef.current) {
      tabModeRef.current = next;
      setTabMode(next);
    }
  }, [tabBarHeight]);

  /** 收起：滚到与「未滚进公告区」一致——占位顶边进入 DOCK 阈值（slotTop > dockTopLine + D） */
  const scrollAnnouncementOutOfView = useCallback(() => {
    if (typeof window === "undefined") return false;
    const slot = tabSlotRef.current;
    if (!slot) return false;
    let H = tabBarRef.current?.offsetHeight || 56;
    // 吸顶 PIN 时条更矮；收起后变为 DOCK，必须用 DOCK 高度算 dockTopLine，否则会少滚、tab 比常态偏低
    if (tabModeRef.current === TAB_MODE.PIN_TOP) {
      H += TAB_BAR_PIN_TO_DOCK_HEIGHT_DELTA;
    }
    const { dockTopLine } = getAnnouncementDockLayout(H);
    const D = 12;
    const targetSlotTop = dockTopLine + D + 2;
    const sr = slot.getBoundingClientRect();
    if (sr.top >= targetSlotTop) return false;
    const docSlotTop = sr.top + window.scrollY;
    const nextTop = Math.max(0, docSlotTop - targetSlotTop);
    const curY = window.scrollY;
    if (Math.abs(curY - nextTop) < 4) return false;
    window.scrollTo({
      top: nextTop,
      behavior: "smooth",
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        recomputeTabModeOnce();
      });
    });
    return true;
  }, [recomputeTabModeOnce]);

  useIsoLayoutEffect(() => {
    const el = tabBarRef.current;
    if (!el) return undefined;

    const measure = () => {
      setTabBarHeight(el.offsetHeight);
      recomputeTabModeOnce();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [activeTab, t, recomputeTabModeOnce]);

  /**
   * Tab 三段式：底部常驻 → 进入公告区随内容滚动 → 触顶吸在页面顶部（仅随滚动/resize 批处理）
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let rafId = 0;

    const tick = () => {
      rafId = 0;
      recomputeTabModeOnce();
    };

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [tabBarHeight, recomputeTabModeOnce]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = bodyRef.current;
    if (!el) return undefined;

    const apply = (entry) => {
      const vis = !!(entry && entry.isIntersecting && entry.intersectionRatio > 0.02);
      bodyInViewportRef.current = vis;
      setBodyInViewport(vis);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        apply(entries[0]);
      },
      { root: null, rootMargin: "0px", threshold: [0, 0.02, 0.05, 0.1, 0.25] }
    );
    obs.observe(el);

    return () => obs.disconnect();
  }, [
    activeTab,
    announcementLoading,
    newsLoading,
    academyLoading,
  ]);

  const handleBodyToggleClick = useCallback(() => {
    const showCollapseArrow =
      bodyInViewportRef.current && !postCollapseExpectExpand;
    if (showCollapseArrow) {
      const didScroll = scrollAnnouncementOutOfView();
      if (didScroll) {
        setPostCollapseExpectExpand(true);
      }
      return;
    }
    setPostCollapseExpectExpand(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        maybeScrollAnnouncementToAnchor();
      });
    });
  }, [
    maybeScrollAnnouncementToAnchor,
    postCollapseExpectExpand,
    scrollAnnouncementOutOfView,
  ]);

  const handleTabClick = useCallback(
    (tabKey, tabElement) => {
      setPostCollapseExpectExpand(false);
      setActiveTab(tabKey);
      if (typeof window === "undefined") return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          /** 关闭模式（底栏吸底 DOCK）：切 tab 才锚点滚页；展开模式（FLOW/PIN_TOP）不触发纵向滚动 */
          if (tabModeRef.current === TAB_MODE.DOCK_BOTTOM) {
            maybeScrollAnnouncementToAnchor();
          }
          if (tabElement) {
            /** scrollIntoView 随文档 dir（含 ar RTL）正确处理横向滚动；scrollLeft 在 RTL 下各浏览器不一致 */
            tabElement.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "center",
            });
          }
        });
      });
    },
    [maybeScrollAnnouncementToAnchor]
  );

  const moreUrl = getMoreUrl(activeTab);
  /** 公告 / 学院：无列表数据时仍展示「查看更多」外链 */
  const showBottomViewMore = !!moreUrl;

  const showCollapseArrow =
    bodyInViewport && !postCollapseExpectExpand;

  const renderNewsTimeline = () => {
    if (newsLoading) return null;
    if (!newsGrouped.length) return null;
    return (
      <div className={styles["announcement__timeline"]}>
        <div className={styles["announcement__timeline-rail"]} aria-hidden />
        {newsGrouped.map((group) => (
          <div key={group.dayKey} className={styles["announcement__timeline-group"]}>
            <div className={styles["announcement__timeline-day-row"]}>
              <span className={styles["announcement__timeline-diamond"]} aria-hidden />
              <span className={styles["announcement__timeline-day-label"]}>
                {group.label}
              </span>
            </div>
            {group.items.map((item) => (
              <div key={item.id} className={styles["announcement__timeline-item"]}>
                <span className={styles["announcement__timeline-node"]} aria-hidden />
                <button
                  type="button"
                  className={styles["announcement__timeline-card"]}
                  onClick={() => openUrl(item.url)}
                >
                  <div className={styles["announcement__timeline-time"]}>
                    {item.timeStr}
                  </div>
                  <span className={styles["announcement__timeline-title"]}>
                    {item.title}
                  </span>
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderSimpleList = () => {
    const { list, loading } = currentState;
    if (loading) return null;
    if (!list.length) return null;
    const isAcademy = activeTab === TAB_KEYS.ACADEMY;

    if (isAcademy) {
      return (
        <div className={styles["announcement__list"]}>
          {list.map((item) => {
            const hasCover = !!(item.coverImageSrc && String(item.coverImageSrc).trim());
            return (
            <button
              key={item.id}
              type="button"
              className={`${styles["announcement__item-row"]} ${styles["announcement__item-row--academy"]}`}
              onClick={() => openUrl(item.url)}
            >
              <div
                className={`${styles["announcement__academy-main"]} ${
                  hasCover ? "" : styles["announcement__academy-main--no-cover"]
                }`}
              >
                <div className={styles["announcement__academy-text-col"]}>
                  <span className={styles["announcement__academy-title"]}>
                    {item.title}
                  </span>
                  {item.tags && item.tags.length > 0 ? (
                    <div className={styles["announcement__academy-tags"]}>
                      {item.tags.map((tag, idx) => (
                        <span
                          key={`${item.id}-tag-${idx}-${tag}`}
                          className={styles["announcement__academy-tag"]}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {hasCover ? (
                  <div
                    className={styles["announcement__academy-cover-wrap"]}
                    data-academy-cover=""
                  >
                    <img
                      src={item.coverImageSrc}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={100}
                      height={75}
                      onError={(e) => {
                        const wrap = e.currentTarget.closest("[data-academy-cover]");
                        if (wrap) wrap.style.display = "none";
                        const main = wrap?.parentElement;
                        if (main && main.classList) {
                          main.classList.add(styles["announcement__academy-main--no-cover"]);
                        }
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className={styles["announcement__list"]}>
        {list.map((item) => (
          <button
            key={item.id}
            type="button"
            className={styles["announcement__item-row"]}
            onClick={() => openUrl(item.url)}
          >
            <div className={styles["announcement__item-announce-block"]}>
              <div className={styles["announcement__item-announce-title-line"]}>
                <img
                  src={iconAnnouncementItemTitle}
                  alt=""
                  className={styles["announcement__item-title-icon"]}
                  width={15}
                  height={15}
                />
                <span className={styles["announcement__title-text"]}>
                  {item.title}
                </span>
              </div>
              {item.date ? (
                <span
                  className={`${styles["announcement__date"]} ${styles["announcement__date--announce-under-title"]}`}
                >
                  {item.date}
                </span>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    );
  };

  const dockOrPin =
    tabMode === TAB_MODE.DOCK_BOTTOM || tabMode === TAB_MODE.PIN_TOP;

  return (
    <div ref={announcementRootRef} className={styles["announcement"]}>
      <div className={styles["announcement__container"]}>
        <div
          ref={tabSlotRef}
          className={styles["announcement__tab-slot"]}
          style={{
            minHeight: dockOrPin ? tabBarHeight || 56 : undefined,
          }}
        >
          <div
            ref={tabBarRef}
            className={`${styles["announcement__sticky"]} ${
              tabMode === TAB_MODE.DOCK_BOTTOM
                ? styles["announcement__sticky--dock-bottom"]
                : tabMode === TAB_MODE.PIN_TOP
                ? styles["announcement__sticky--pin-top"]
                : styles["announcement__sticky--flow"]
            }`}
          >
          <div className={styles["announcement__sticky-inner"]}>
            <div className={styles["announcement__header"]}>
              <div className={styles["announcement__tabs-scroll"]}>
                <div className={styles["announcement__tabs"]}>
                  {tabs.map((tab) => (
                    <div
                      key={tab.key}
                      className={`${styles["announcement__tab"]} ${
                        activeTab === tab.key
                          ? styles["announcement__tab--active"]
                          : ""
                      }`}
                      onClick={(e) => handleTabClick(tab.key, e.currentTarget)}
                      role="tab"
                      aria-selected={activeTab === tab.key}
                    >
                      {tab.label}
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className={styles["announcement__body-toggle"]}
                onClick={handleBodyToggleClick}
                aria-label={
                  showCollapseArrow
                    ? t("home.announcementBodyCollapse") ||
                      "Collapse announcement section"
                    : t("home.announcementBodyExpand") ||
                      "Scroll to expand announcement section"
                }
              >
                <img
                  src={
                    showCollapseArrow
                      ? iconAnnouncementBodyDown
                      : iconAnnouncementBodyUp
                  }
                  alt=""
                  width={16}
                  height={16}
                />
              </button>
            </div>
          </div>
        </div>
        </div>

        <div ref={bodyRef} className={styles["announcement__body"]}>
          <div className={styles["announcement__scroll-fill"]}>
            {currentState.loading ? null : activeTab === TAB_KEYS.NEWS ? (
              newsList.length === 0 ? (
                <div className={styles["announcement__empty"]}>
                  {t("home.noListInfo") || "暂无信息"}
                </div>
              ) : (
                <>
                  {renderNewsTimeline()}
                  {newsGrouped.length > 0 ? (
                    <div className={styles["announcement__no-more"]}>
                      {t("home.noMoreContent") || "No more content"}
                    </div>
                  ) : null}
                </>
              )
            ) : currentState.list.length === 0 ? (
              <div className={styles["announcement__empty"]}>
                {t("home.noListInfo") || "暂无信息"}
              </div>
            ) : (
              renderSimpleList()
            )}
          </div>

          {showBottomViewMore && (
            <div className={styles["announcement__view-more-row"]}>
              <button
                type="button"
                className={styles["announcement__view-more-link"]}
                onClick={onMoreClick}
              >
                {t("home.ViewMore") || "View More"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(HomeAnnouncement);
